/**
 * Evite's ApiClient
 * @class ApiClient
 */
export class ApiClient {
  /**
   * Base URL
   * @type {string}
   */
  baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Makes an HTTP request to the API
   * @param {string} path - The endpoint URL (relative to baseUrl)
   * @param {RequestInit} options - Fetch options for the request
   * @returns {Promise<any>} The parsed JSON response
   * @throws {Error} HTTP Error with status and response details when request fails
   */
  async request(path: string, options: RequestInit) {
    const response = await evite.fetch(`${this.baseUrl}${path}`, options);
    if (!response.ok) {
      const error = new Error('HTTP Error');
      error.message = `response status: ${response.status}`;
      error.cause = await response.json();
      throw error;
    }

    return response;
  }

  /**
   * Makes a GET request to the API
   * @param {string} path - The endpoint URL (relative to baseUrl)
   * @param {RequestInit} [options={}] - Additional fetch options to merge with defaults
   * @returns {Promise<any>} The parsed JSON response
   */
  async get(path: string, options: RequestInit = {}) {
    return this.request(path, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
      ...options,
    });
  }

  /**
   * Makes a POST request to the API with JSON data
   * @param {string} path - The endpoint URL (relative to baseUrl)
   * @param {unknown} data - The data to send in the request body (will be JSON stringified)
   * @param {RequestInit} [options={}] - Additional fetch options to merge with defaults
   * @returns {Promise<any>} The parsed JSON response
   */
  async post(path: string, data: unknown, options: RequestInit = {}) {
    return this.request(path, {
      method: 'POST',
      body: JSON.stringify(data),
      credentials: 'include',
      ...options,
      headers: {
        Accept: 'application/json',
        'X-CSRFToken': evite.cookie('csrftoken') || '',
        'Content-Type': 'application/json',
        ...(options?.headers || {}),
      },
    });
  }

  /**
   * Makes a PUT request to the API with JSON data
   * @param {string} path - The endpoint URL (relative to baseUrl)
   * @param {unknown} data - The data to send in the request body (will be JSON stringified)
   * @param {RequestInit} [options={}] - Additional fetch options to merge with defaults
   * @returns {Promise<any>} The parsed JSON response
   */
  async put(path: string, data: unknown, options: RequestInit = {}) {
    return this.request(path, {
      method: 'PUT',
      body: JSON.stringify(data),
      credentials: 'include',
      ...options,
      headers: {
        Accept: 'application/json',
        'X-CSRFToken': evite.cookie('csrftoken') || '',
        'Content-Type': 'application/json',
        ...(options?.headers || {}),
      },
    });
  }

  /**
   * Makes a DELETE request to the API with JSON data
   * @param {string} path - The endpoint URL (relative to baseUrl)
   * @param {RequestInit} [options={}] - Additional fetch options to merge with defaults
   * @returns {Promise<any>} The parsed JSON response
   */
  async delete(path: string, options: RequestInit = {}) {
    return this.request(path, {
      method: 'DELETE',
      credentials: 'include',
      ...options,
      headers: {
        Accept: 'application/json',
        'X-CSRFToken': evite.cookie('csrftoken') || '',
        ...(options?.headers || {}),
      },
    });
  }
}

// NOTE: I'm purposely setting an empty string for the baseUrl.
// Maybe in a distant future, we want the baseUrl to be "/v2"
export const apiClient = new ApiClient('');
